import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import { CONNECTIONS_QUERY_KEY, useConnectionsData } from "./hooks/useConnectionsData";
import {
  activateAiProvider,
  createAiBackend,
  createInfrastructureService,
  createNewsProvider,
  updateAiBackend,
  updateInfrastructureService,
  updateNewsProvider,
  deleteAiBackend,
  deleteInfrastructureService,
  deleteNewsProvider,
  fetchAiFailoverHistory,
  fetchAiProviders,
  fetchUsageAnalytics,
  type ActivateAiProviderInput,
  type CreateAiBackendInput,
  type CreateInfrastructureServiceInput,
  type CreateNewsProviderInput,
  type UpdateAiBackendInput,
  type UpdateInfrastructureServiceInput,
  type UpdateNewsProviderInput,
} from "@app/services/connectionsApi";
import { createSecret as createSecretRecord } from "@app/services/secretsApi";
import type {
  ConnectionStatus,
  ConnectionsPayload,
  ConnectionsUsageAnalytics,
  StatusBreakdown,
} from "@shared/types/connections";
import type { AiProvider } from "@shared/types/aiProviders";
import "./connections.css";

const STATUS_OPTIONS: ConnectionStatus[] = ["online", "degraded", "offline", "unknown"];
const ALERT_PREFS_KEY = "dashboard.connections.alertPrefs";

type AlertPreferenceKey = "inApp" | "telegram" | "emailDigest";

type AlertPreferences = Record<AlertPreferenceKey, boolean>;

const DEFAULT_ALERT_PREFS: AlertPreferences = {
  inApp: true,
  telegram: true,
  emailDigest: false,
};

const DEFAULT_MANIFEST_TEMPLATE = `{
  "services": [
    {
      "name": "Redis Cache",
      "kind": "cache",
      "endpoint": "redis://localhost:6379/0",
      "status": "online",
      "ownerTeam": "platform",
      "notes": "Primary cache for AI pipelines"
    }
  ]
}`;

type NewsFormState = {
  name: string;
  category: string;
  status: ConnectionStatus;
  authenticationMode: string;
  regions: string;
  dataTypes: string;
  notes: string;
  apiKey: string;
};

type AiFormState = {
  name: string;
  provider: string;
  model: string;
  status: ConnectionStatus;
  failoverPriority: string;
  baseUrl: string;
  notes: string;
  apiKey: string;
};

type InfraFormState = {
  name: string;
  kind: string;
  endpoint: string;
  status: ConnectionStatus;
  ownerTeam: string;
  notes: string;
};

function statusTone(status: ConnectionStatus | undefined) {
  switch (status) {
    case "online":
      return "success";
    case "degraded":
      return "warning";
    case "offline":
      return "danger";
    default:
      return "neutral";
  }
}

function formatTimestamp(value: string | null | undefined) {
  if (!value) {
    return "Never";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    // Use fixed format independent of browser/OS locale: YYYY-MM-DD HH:mm:ss
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return value;
  }
}

function renderWarnings(payload: ConnectionsPayload | undefined) {
  if (!payload?.warnings?.length) {
    return null;
  }
  return (
    <ul className="connections-hint">
      {payload.warnings.map((warning) => (
        <li key={warning}>{warning}</li>
      ))}
    </ul>
  );
}

function renderStatusBreakdown(breakdown: StatusBreakdown) {
  return (
    <div className="connections-usage-status-grid">
      <div>
        <span>Online</span>
        <strong>{breakdown.online}</strong>
      </div>
      <div>
        <span>Degraded</span>
        <strong>{breakdown.degraded}</strong>
      </div>
      <div>
        <span>Offline</span>
        <strong>{breakdown.offline}</strong>
      </div>
      <div>
        <span>Unknown</span>
        <strong>{breakdown.unknown}</strong>
      </div>
    </div>
  );
}

const initialNewsForm: NewsFormState = {
  name: "",
  category: "",
  status: "online",
  authenticationMode: "",
  regions: "",
  dataTypes: "",
  notes: "",
  apiKey: "",
};

const initialAiForm: AiFormState = {
  name: "",
  provider: "",
  model: "",
  status: "unknown",
  failoverPriority: "",
  baseUrl: "",
  notes: "",
  apiKey: "",
};

const initialInfraForm: InfraFormState = {
  name: "",
  kind: "",
  endpoint: "",
  status: "unknown",
  ownerTeam: "",
  notes: "",
};

export function ConnectionsTab(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useConnectionsData();
  const queryClient = useQueryClient();

  const [isNewsFormOpen, setNewsFormOpen] = useState(false);
  const [newsForm, setNewsForm] = useState<NewsFormState>(initialNewsForm);
  const [newsError, setNewsError] = useState<string | null>(null);
  const [editingNewsId, setEditingNewsId] = useState<string | null>(null);

  const [isAiFormOpen, setAiFormOpen] = useState(false);
  const [aiForm, setAiForm] = useState<AiFormState>(initialAiForm);
  const [aiError, setAiError] = useState<string | null>(null);
  const [editingAiId, setEditingAiId] = useState<string | null>(null);

  const [isInfraFormOpen, setInfraFormOpen] = useState(false);
  const [infraForm, setInfraForm] = useState<InfraFormState>(initialInfraForm);
  const [infraError, setInfraError] = useState<string | null>(null);
  const [editingInfraId, setEditingInfraId] = useState<string | null>(null);
  const [globalNotice, setGlobalNotice] = useState<string | null>(null);
  const [providerActionMessage, setProviderActionMessage] = useState<string | null>(null);
  const [providerActionError, setProviderActionError] = useState<string | null>(null);
  const [isUsagePanelOpen, setUsagePanelOpen] = useState(false);
  const [isManifestModalOpen, setManifestModalOpen] = useState(false);
  const [manifestText, setManifestText] = useState(DEFAULT_MANIFEST_TEMPLATE);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [manifestFeedback, setManifestFeedback] = useState<string | null>(null);
  const [isManifestImporting, setManifestImporting] = useState(false);
  const [isAlertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState<AlertPreferences>(DEFAULT_ALERT_PREFS);
  const [alertNotice, setAlertNotice] = useState<string | null>(null);

  const newsMutation = useMutation({
    mutationFn: (input: CreateNewsProviderInput) => createNewsProvider(input),
  });

  const updateNewsMutation = useMutation({
    mutationFn: (variables: { id: string; input: UpdateNewsProviderInput }) =>
      updateNewsProvider(variables.id, variables.input),
  });

  const deleteNewsMutation = useMutation({
    mutationFn: (providerId: string) => deleteNewsProvider(providerId),
  });

  const aiMutation = useMutation({
    mutationFn: (input: CreateAiBackendInput) => createAiBackend(input),
  });

  const updateAiMutation = useMutation({
    mutationFn: (variables: { id: string; input: UpdateAiBackendInput }) =>
      updateAiBackend(variables.id, variables.input),
  });

  const deleteAiMutation = useMutation({
    mutationFn: (backendId: string) => deleteAiBackend(backendId),
  });

  const infraMutation = useMutation({
    mutationFn: (input: CreateInfrastructureServiceInput) => createInfrastructureService(input),
  });

  const updateInfraMutation = useMutation({
    mutationFn: (variables: { id: string; input: UpdateInfrastructureServiceInput }) =>
      updateInfrastructureService(variables.id, variables.input),
  });

  const deleteInfraMutation = useMutation({
    mutationFn: (serviceId: string) => deleteInfrastructureService(serviceId),
  });

  const aiProvidersQuery = useQuery({
    queryKey: ["dashboard", "connections", "ai-providers"],
    queryFn: fetchAiProviders,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const failoverHistoryQuery = useQuery({
    queryKey: ["dashboard", "connections", "ai-providers", "failovers"],
    queryFn: () => fetchAiFailoverHistory(20),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const usageAnalyticsQuery = useQuery<ConnectionsUsageAnalytics>({
    queryKey: ["dashboard", "connections", "usage-analytics"],
    queryFn: fetchUsageAnalytics,
    enabled: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const {
    data: usageAnalytics,
    isFetching: isUsageFetching,
    isError: isUsageError,
    error: usageAnalyticsError,
    refetch: refetchUsageAnalytics,
  } = usageAnalyticsQuery;

  useEffect(() => {
    if (isUsagePanelOpen) {
      void refetchUsageAnalytics();
    }
  }, [isUsagePanelOpen, refetchUsageAnalytics]);

  const usageErrorMessage =
    usageAnalyticsError instanceof Error ? usageAnalyticsError.message : null;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const stored = window.localStorage.getItem(ALERT_PREFS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AlertPreferences;
        setAlertPrefs({
          ...DEFAULT_ALERT_PREFS,
          ...parsed,
        });
      }
    } catch (error) {
      console.warn("[ConnectionsTab] Failed to load alert preferences", error);
    }
  }, []);

  const persistAlertPrefs = (next: AlertPreferences) => {
    setAlertPrefs(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(ALERT_PREFS_KEY, JSON.stringify(next));
        setAlertNotice("Alert preferences saved.");
      } catch (error) {
        console.warn("[ConnectionsTab] Failed to persist alert preferences", error);
        setAlertNotice("Failed to persist preferences locally.");
      }
    }
  };

  const handleAlertToggle = (key: AlertPreferenceKey) => (event: ChangeEvent<HTMLInputElement>) => {
    persistAlertPrefs({
      ...alertPrefs,
      [key]: event.target.checked,
    });
  };

  const handleResetAlerts = () => {
    persistAlertPrefs(DEFAULT_ALERT_PREFS);
  };

  const handleExportLog = () => {
    if (!data || data.changeLog.length === 0) {
      setGlobalNotice("No change log entries available to export yet.");
      return;
    }
    try {
      const exportPayload = {
        generatedAt: new Date().toISOString(),
        summary: data.summary,
        changeLog: data.changeLog,
      };
      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `connections-change-log-${new Date().toISOString()}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      setGlobalNotice("Change log exported successfully.");
    } catch (error) {
      console.error("[ConnectionsTab] Failed to export change log", error);
      setGlobalNotice("Failed to export change log.");
    }
  };

  const closeManifestModal = () => {
    setManifestModalOpen(false);
    setManifestImporting(false);
    setManifestError(null);
    setManifestFeedback(null);
  };

  const handleManifestImport = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setManifestError(null);
    setManifestFeedback(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(manifestText);
    } catch (error) {
      setManifestError("Manifest is not valid JSON. Please review and try again.");
      return;
    }

    const servicesPayload =
      Array.isArray(parsed) && parsed.every((item) => typeof item === "object" && item !== null)
        ? (parsed as Array<Record<string, unknown>>)
        : typeof parsed === "object" &&
            parsed !== null &&
            Array.isArray((parsed as { services?: unknown }).services)
          ? ((parsed as { services: Array<Record<string, unknown>> }).services ?? [])
          : null;

    if (!servicesPayload || servicesPayload.length === 0) {
      setManifestError("Manifest must include a non-empty 'services' array or be an array of services.");
      return;
    }

    let normalized: Array<{
      name: string;
      kind: string;
      endpoint?: string;
      status: ConnectionStatus;
      ownerTeam?: string;
      notes?: string;
      secretId?: string;
    }>;

    try {
      normalized = servicesPayload.map((service, index) => {
        const name = typeof service.name === "string" ? service.name.trim() : "";
        const kind = typeof service.kind === "string" ? service.kind.trim() : "";
        if (!name || !kind) {
          throw new Error(`Service #${index + 1} is missing a required 'name' or 'kind' property.`);
        }
        const status =
          typeof service.status === "string" && STATUS_OPTIONS.includes(service.status as ConnectionStatus)
            ? (service.status as ConnectionStatus)
            : "unknown";
        return {
          name,
          kind,
          endpoint: typeof service.endpoint === "string" ? service.endpoint : undefined,
          status,
          ownerTeam: typeof service.ownerTeam === "string" ? service.ownerTeam : undefined,
          notes: typeof service.notes === "string" ? service.notes : undefined,
          secretId: typeof service.secretId === "string" ? service.secretId : undefined,
        };
      });
    } catch (error) {
      setManifestError(error instanceof Error ? error.message : "Manifest validation failed.");
      return;
    }

    setManifestImporting(true);
    try {
      const results = await Promise.allSettled(
        normalized.map((entry) =>
          createInfrastructureService({
            name: entry.name,
            kind: entry.kind,
            endpoint: entry.endpoint,
            status: entry.status,
            ownerTeam: entry.ownerTeam,
            notes: entry.notes,
            secretId: entry.secretId ?? null,
          }),
        ),
      );

      const succeeded = results.filter((result) => result.status === "fulfilled").length;
      const failures = results
        .map((result, index) =>
          result.status === "rejected"
            ? {
                index,
                reason: result.reason instanceof Error ? result.reason.message : String(result.reason),
              }
            : null,
        )
        .filter(Boolean) as Array<{ index: number; reason: string }>;

      if (failures.length === 0) {
        setManifestFeedback(`Imported ${succeeded} infrastructure service${succeeded === 1 ? "" : "s"} successfully.`);
        setGlobalNotice(`Imported ${succeeded} infrastructure service${succeeded === 1 ? "" : "s"} from manifest.`);
        closeManifestModal();
        queryClient.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY });
      } else {
        const failureSummary = failures
          .map((failure) => `#${failure.index + 1}: ${failure.reason}`)
          .join("; ");
        setManifestFeedback(`Imported ${succeeded} entries. Failed ${failures.length}: ${failureSummary}`);
      }
    } catch (error) {
      setManifestError(error instanceof Error ? error.message : "Manifest import failed.");
    } finally {
      setManifestImporting(false);
    }
  };

  const activateProviderMutation = useMutation({
    mutationFn: (input: ActivateAiProviderInput) => activateAiProvider(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard", "connections", "ai-providers"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "connections", "ai-providers", "failovers"],
      });
    },
  });

  const summary = data?.summary;
  const newsSection = data?.newsProviders;
  const aiSection = data?.aiBackends;
  const infraSection = data?.infrastructureServices;
  const changeLog = data?.changeLog ?? [];
  const aiProviders = aiProvidersQuery.data?.providers ?? [];
  const failoverEntries = failoverHistoryQuery.data?.entries ?? [];

  const isNewsSaving = newsMutation.isPending || updateNewsMutation.isPending;
  const isAiSaving = aiMutation.isPending || updateAiMutation.isPending;
  const isInfraSaving = infraMutation.isPending || updateInfraMutation.isPending;

  const handleNewsSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNewsError(null);

    if (!newsForm.name.trim()) {
      setNewsError("Provider name is required.");
      return;
    }

    const regions = newsForm.regions
      .split(",")
      .map((region) => region.trim())
      .filter(Boolean);
    const dataTypes = newsForm.dataTypes
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);

    try {
      const authModeRaw = newsForm.authenticationMode.trim();
      const notesRaw = newsForm.notes.trim();
      const authenticationModeValue = authModeRaw.length > 0 ? authModeRaw : undefined;
      const notesValue = notesRaw.length > 0 ? notesRaw : undefined;
      const createPayload: CreateNewsProviderInput = {
        name: newsForm.name.trim(),
        category: newsForm.category.trim() || "general",
        status: newsForm.status,
        authenticationMode: authenticationModeValue,
        regions,
        dataTypes,
        notes: notesValue,
      };

      let responsePayload: ConnectionsPayload;
      if (editingNewsId) {
        responsePayload = await updateNewsMutation.mutateAsync({
          id: editingNewsId,
          input: {
            ...createPayload,
            authenticationMode: authenticationModeValue ?? null,
            notes: notesValue ?? null,
          },
        });
        setGlobalNotice(`Provider "${createPayload.name}" updated successfully.`);
      } else {
        responsePayload = await newsMutation.mutateAsync(createPayload);
        const apiKey = newsForm.apiKey.trim();
        if (apiKey.length > 0) {
          const provider = responsePayload.newsProviders.items.find(
            (item) => item.name === newsForm.name.trim(),
          );
          if (provider) {
            try {
              await createSecretRecord({
                secretId: provider.id,
                label: provider.name,
                secretType: "news",
                value: apiKey,
                metadata: {
                  category: provider.category,
                  authenticationMode: provider.authenticationMode ?? "unknown",
                },
              });
              setGlobalNotice("Provider and API key stored successfully.");
            } catch (secretError) {
              console.error(secretError);
              setGlobalNotice(
                "Provider saved but API key could not be stored. Please retry via the security panel.",
              );
            }
          } else {
            setGlobalNotice(
              "Provider saved but the new entry could not be located for key storage.",
            );
          }
        } else {
          setGlobalNotice("Provider registered successfully.");
        }
      }

      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, responsePayload);
      setNewsForm(initialNewsForm);
      setNewsFormOpen(false);
      setEditingNewsId(null);
      setNewsError(null);
    } catch (mutationError) {
      if (mutationError instanceof Error) {
        setNewsError(mutationError.message);
      } else {
        setNewsError("Failed to add provider.");
      }
    }
  };

  const handleAiSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAiError(null);

    if (!aiForm.name.trim() || !aiForm.provider.trim()) {
      setAiError("Name and provider are required.");
      return;
    }

    const failoverInput = aiForm.failoverPriority.trim();
    let failoverPriorityValue: number | undefined;
    if (failoverInput.length > 0) {
      const parsed = Number(failoverInput);
      if (Number.isNaN(parsed)) {
        setAiError("Failover priority must be a number.");
        return;
      }
      failoverPriorityValue = parsed;
    }

    try {
      const modelRaw = aiForm.model.trim();
      const notesRaw = aiForm.notes.trim();
      const modelValue = modelRaw.length > 0 ? modelRaw : undefined;
      const notesValue = notesRaw.length > 0 ? notesRaw : undefined;
      const basePayload: CreateAiBackendInput = {
        name: aiForm.name.trim(),
        provider: aiForm.provider.trim(),
        model: modelValue,
        status: aiForm.status,
        failoverPriority: failoverPriorityValue,
        baseUrl: aiForm.baseUrl.trim() || undefined,
        notes: notesValue,
      };

      let responsePayload: ConnectionsPayload;
      if (editingAiId) {
        responsePayload = await updateAiMutation.mutateAsync({
          id: editingAiId,
          input: {
            ...basePayload,
            baseUrl: aiForm.baseUrl.trim() || null,
            model: modelValue ?? null,
            failoverPriority: failoverPriorityValue ?? null,
            notes: notesValue ?? null,
          },
        });
        setGlobalNotice(`AI backend "${basePayload.name}" updated successfully.`);
      } else {
        responsePayload = await aiMutation.mutateAsync(basePayload);

        const apiKey = aiForm.apiKey.trim();
        if (apiKey.length > 0) {
          const backend = responsePayload.aiBackends.items.find(
            (item) =>
              item.name === aiForm.name.trim() && item.provider === aiForm.provider.trim(),
          );
          if (backend) {
            try {
              await createSecretRecord({
                secretId: backend.id,
                label: backend.name,
                secretType: "ai",
                value: apiKey,
                metadata: {
                  provider: backend.provider,
                  model: backend.model ?? "unknown",
                },
              });
              setGlobalNotice("AI backend and API key stored successfully.");
            } catch (secretError) {
              console.error(secretError);
              setGlobalNotice(
                "AI backend saved but API key could not be stored. Please retry via the security panel.",
              );
            }
          } else {
            setGlobalNotice(
              "AI backend saved but the new entry could not be located for key storage.",
            );
          }
        } else {
          setGlobalNotice("AI backend registered successfully.");
        }
      }

      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, responsePayload);
      setAiForm(initialAiForm);
      setAiFormOpen(false);
      setEditingAiId(null);
      setAiError(null);
      queryClient.invalidateQueries({ queryKey: ["dashboard", "connections", "ai-providers"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "connections", "ai-providers", "failovers"],
      });
    } catch (mutationError) {
      if (mutationError instanceof Error) {
        setAiError(mutationError.message);
      } else {
        setAiError("Failed to register AI backend.");
      }
    }
  };

  const handleInfraSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setInfraError(null);

    if (!infraForm.name.trim() || !infraForm.kind.trim()) {
      setInfraError("Service name and kind are required.");
      return;
    }

    try {
      const endpointRaw = infraForm.endpoint.trim();
      const ownerRaw = infraForm.ownerTeam.trim();
      const notesRaw = infraForm.notes.trim();
      const createPayload: CreateInfrastructureServiceInput = {
        name: infraForm.name.trim(),
        kind: infraForm.kind.trim(),
        endpoint: endpointRaw.length > 0 ? endpointRaw : undefined,
        status: infraForm.status,
        ownerTeam: ownerRaw.length > 0 ? ownerRaw : undefined,
        notes: notesRaw.length > 0 ? notesRaw : undefined,
      };
      let responsePayload: ConnectionsPayload;
      if (editingInfraId) {
        responsePayload = await updateInfraMutation.mutateAsync({
          id: editingInfraId,
          input: {
            ...createPayload,
            endpoint: endpointRaw.length > 0 ? endpointRaw : null,
            ownerTeam: ownerRaw.length > 0 ? ownerRaw : null,
            notes: notesRaw.length > 0 ? notesRaw : null,
          },
        });
        setGlobalNotice(`Service "${createPayload.name}" updated successfully.`);
      } else {
        responsePayload = await infraMutation.mutateAsync(createPayload);
        setGlobalNotice("Infrastructure service registered successfully.");
      }
      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, responsePayload);
      setInfraForm(initialInfraForm);
      setInfraFormOpen(false);
      setEditingInfraId(null);
      setInfraError(null);
    } catch (mutationError) {
      if (mutationError instanceof Error) {
        setInfraError(mutationError.message);
      } else {
        setInfraError("Failed to register service.");
      }
    }
  };

  const handleNewsEdit = (providerId: string) => {
    const provider = newsSection?.items.find((item) => item.id === providerId);
    if (!provider) {
      return;
    }
    setNewsForm({
      name: provider.name,
      category: provider.category ?? "",
      status: provider.status,
      authenticationMode: provider.authenticationMode ?? "",
      regions: (provider.regions ?? []).join(", "),
      dataTypes: (provider.dataTypes ?? []).join(", "),
      notes: provider.notes ?? "",
      apiKey: "",
    });
    setEditingNewsId(providerId);
    setNewsFormOpen(true);
    setNewsError(null);
    setGlobalNotice(null);
  };

  const handleNewsDelete = async (providerId: string, providerName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove the news provider "${providerName}"? This action is audited.`,
      )
    ) {
      return;
    }
    setNewsError(null);
    try {
      const payload = await deleteNewsMutation.mutateAsync(providerId);
      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, payload);
      setGlobalNotice(`Provider "${providerName}" removed.`);
      if (editingNewsId === providerId) {
        setEditingNewsId(null);
        setNewsForm(initialNewsForm);
        setNewsFormOpen(false);
      }
    } catch (error) {
      setNewsError(error instanceof Error ? error.message : "Failed to delete provider.");
    }
  };

  const handleAiEdit = (backendId: string) => {
    const backend = aiSection?.items.find((item) => item.id === backendId);
    if (!backend) {
      return;
    }
    setAiForm({
      name: backend.name,
      provider: backend.provider,
      model: backend.model ?? "",
      status: backend.status,
      failoverPriority:
        backend.failoverPriority != null ? String(backend.failoverPriority) : "",
      baseUrl: backend.baseUrl ?? "",
      notes: backend.notes ?? "",
      apiKey: "",
    });
    setEditingAiId(backendId);
    setAiFormOpen(true);
    setAiError(null);
    setGlobalNotice(null);
  };

  const handleAiDelete = async (backendId: string, backendName: string) => {
    if (
      !window.confirm(
        `Remove AI backend "${backendName}"? Manual failover history will remain intact.`,
      )
    ) {
      return;
    }
    setAiError(null);
    try {
      const payload = await deleteAiMutation.mutateAsync(backendId);
      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, payload);
      setGlobalNotice(`AI backend "${backendName}" removed.`);
      if (editingAiId === backendId) {
        setEditingAiId(null);
        setAiForm(initialAiForm);
        setAiFormOpen(false);
      }
      queryClient.invalidateQueries({ queryKey: ["dashboard", "connections", "ai-providers"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "connections", "ai-providers", "failovers"],
      });
    } catch (error) {
      setAiError(error instanceof Error ? error.message : "Failed to delete AI backend.");
    }
  };

  const handleInfraEdit = (serviceId: string) => {
    const service = infraSection?.items.find((item) => item.id === serviceId);
    if (!service) {
      return;
    }
    setInfraForm({
      name: service.name,
      kind: service.kind,
      endpoint: service.endpoint ?? "",
      status: service.status,
      ownerTeam: service.ownerTeam ?? "",
      notes: service.notes ?? "",
    });
    setEditingInfraId(serviceId);
    setInfraFormOpen(true);
    setInfraError(null);
    setGlobalNotice(null);
  };

  const handleInfraDelete = async (serviceId: string, serviceName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove "${serviceName}" from the registry? This disconnects it from automation.`,
      )
    ) {
      return;
    }
    setInfraError(null);
    try {
      const payload = await deleteInfraMutation.mutateAsync(serviceId);
      queryClient.setQueryData(CONNECTIONS_QUERY_KEY, payload);
      setGlobalNotice(`Service "${serviceName}" removed.`);
      if (editingInfraId === serviceId) {
        setEditingInfraId(null);
        setInfraForm(initialInfraForm);
        setInfraFormOpen(false);
      }
    } catch (error) {
      setInfraError(error instanceof Error ? error.message : "Failed to delete service.");
    }
  };

  const handleProviderActivation = async (provider: AiProvider) => {
    setProviderActionError(null);
    setProviderActionMessage(null);

    const reason = window.prompt(
      `Reason for activating "${provider.name}"?`,
      "Manual switch via Connections tab",
    );
    if (!reason || reason.trim().length < 3) {
      setProviderActionError("Switch cancelled. Please provide a reason with at least 3 characters.");
      return;
    }

    const notes = window.prompt("Optional notes (press Cancel to skip).");

    activateProviderMutation.mutate(
      {
        providerId: provider.id,
        initiatedBy: "dashboard-admin",
        reason: reason.trim(),
        notes: notes?.trim() || undefined,
      },
      {
        onSuccess: () => {
          setProviderActionMessage(`Provider "${provider.name}" activated successfully.`);
        },
        onError: (mutationError) => {
          setProviderActionError(mutationError.message);
        },
      },
    );
  };

  return (
    <div className="connections-root">
      <Card
        className="overview-card connections-card"
        title="Operations summary"
        subtitle="Admin-only view of infrastructure connections. All sensitive credentials are stored in the secrets vault."
      >
        <div className="section-legend">
          <span className="section-legend__dot" />
          <div>
            Use this tab to manage shared infrastructure: news ingestion, AI providers, and platform
            services. Keys live in the vault—we only surface metadata and diagnostics here.
          </div>
        </div>

        {isLoading && <div className="integrations-empty">Loading connections…</div>}
        {isError && (
          <div className="integrations-empty">
            Failed to load connection data.{" "}
            <Button variant="link" onClick={() => refetch()}>
              Retry
            </Button>
            <div className="connections-hint">{String(error)}</div>
          </div>
        )}

        {summary && !isLoading && !isError && (
          <>
            <div className="connections-summary">
              <div>
                <strong>{summary.totalNewsProviders}</strong>
                <div className="connections-matrix__meta">News providers</div>
              </div>
              <div>
                <strong>{summary.totalAiBackends}</strong>
                <div className="connections-matrix__meta">AI backends</div>
              </div>
              <div>
                <strong>{summary.totalInfrastructureServices}</strong>
                <div className="connections-matrix__meta">Infrastructure services</div>
              </div>
            </div>
            <p className="connections-hint">{summary.message}</p>
          </>
        )}
        {!isLoading && renderWarnings(data)}
      </Card>

      {globalNotice ? (
        <div className="connections-global-notice connections-provider-alert connections-provider-alert--success">
          {globalNotice}
        </div>
      ) : null}

      <div className="connections-grid">
        <Card
          className="overview-card connections-card"
          title="News & sentiment providers"
          subtitle="Centralized ingest for market-moving headlines."
        >
          <div className="section-legend">
            <span className="section-legend__dot" />
            <div>
              Register the upstream APIs (NewsAPI, AlphaVantage, custom scrapers…). Once configured,
              their health metrics and feed counts will appear here.
            </div>
          </div>
          {newsSection?.items?.length ? (
            <div className="connections-matrix">
              {newsSection.items.map((provider) => (
                <div key={provider.id} className="connections-matrix__row">
                  <div className="connections-matrix__info">
                    <div>{provider.name}</div>
                    <div className="connections-matrix__meta">
                      {provider.category} · Last check {formatTimestamp(provider.lastCheckedAt || null)}
                    </div>
                  </div>
                  <div className="connections-matrix__stack">
                    <Pill tone={statusTone(provider.status)}>{provider.status ?? "unknown"}</Pill>
                    <div className="connections-row-actions">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleNewsEdit(provider.id)}
                        disabled={isNewsSaving}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="connections-delete-button"
                        onClick={() => handleNewsDelete(provider.id, provider.name)}
                        disabled={deleteNewsMutation.isPending}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="integrations-empty">{newsSection?.meta?.emptyStateMessage}</div>
          )}

          {isNewsFormOpen ? (
            <form className="connections-form" onSubmit={handleNewsSubmit}>
              <label>
                Provider name
                <input
                  value={newsForm.name}
                  onChange={(event) => setNewsForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="e.g. CryptoPanic"
                  required
                />
              </label>
              <label>
                Category
                <input
                  value={newsForm.category}
                  onChange={(event) =>
                    setNewsForm((prev) => ({ ...prev, category: event.target.value }))
                  }
                  placeholder="news / sentiment / social"
                />
              </label>
              <label>
                Status
                <select
                  value={newsForm.status}
                  onChange={(event) =>
                    setNewsForm((prev) => ({
                      ...prev,
                      status: event.target.value as ConnectionStatus,
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Authentication mode
                <input
                  value={newsForm.authenticationMode}
                  onChange={(event) =>
                    setNewsForm((prev) => ({ ...prev, authenticationMode: event.target.value }))
                  }
                  placeholder="apiKey / oauth / none"
                />
              </label>
              <label>
                Regions
                <input
                  value={newsForm.regions}
                  onChange={(event) => setNewsForm((prev) => ({ ...prev, regions: event.target.value }))}
                  placeholder="Comma separated"
                />
              </label>
              <label>
                Data types
                <input
                  value={newsForm.dataTypes}
                  onChange={(event) =>
                    setNewsForm((prev) => ({ ...prev, dataTypes: event.target.value }))
                  }
                  placeholder="headlines, sentiment"
                />
              </label>
              <label>
                API key (optional)
                <input
                  value={newsForm.apiKey}
                  onChange={(event) =>
                    setNewsForm((prev) => ({ ...prev, apiKey: event.target.value }))
                  }
                  placeholder={
                    editingNewsId ? "Manage rotations via Security ▸ Secrets" : "Paste provider API key"
                  }
                  disabled={editingNewsId !== null}
                />
              </label>
              <label>
                Notes
                <textarea
                  value={newsForm.notes}
                  onChange={(event) => setNewsForm((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={3}
                />
              </label>
              {newsError ? <p className="connections-form__error">{newsError}</p> : null}
              <div className="connections-form__actions">
                <Button type="submit" variant="primary" disabled={isNewsSaving}>
                  {isNewsSaving
                    ? "Saving…"
                    : editingNewsId
                      ? "Update provider"
                      : "Save provider"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setNewsFormOpen(false);
                    setNewsError(null);
                    setNewsForm(initialNewsForm);
                    setEditingNewsId(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="integrations-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setNewsForm(initialNewsForm);
                  setEditingNewsId(null);
                  setNewsFormOpen(true);
                }}
              >
                Add provider
              </Button>
            </div>
          )}
        </Card>

        <Card
          className="overview-card connections-card"
          title="AI backends"
          subtitle="Models powering research summaries, compliance filters, and execution heuristics."
        >
          <div className="section-legend">
            <span className="section-legend__dot" />
            <div>
              Provision LLM endpoints or internal models. As soon as a backend is online, the usage
              and token budgets will populate this panel.
            </div>
          </div>
          {aiSection?.items?.length ? (
            <div className="connections-matrix">
              {aiSection.items.map((backend) => (
                <div key={backend.id} className="connections-matrix__row">
                  <div className="connections-matrix__info">
                    <div>{backend.name}</div>
                    <div className="connections-matrix__meta">
                      Provider {backend.provider}
                      {backend.model ? ` · Model ${backend.model}` : ""}
                      {" · Last check "}
                      {formatTimestamp(backend.lastCheckedAt || null)}
                    </div>
                  </div>
                  <div className="connections-matrix__stack">
                    <Pill tone={statusTone(backend.status)}>{backend.status ?? "unknown"}</Pill>
                    <div className="connections-row-actions">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => handleAiEdit(backend.id)}
                        disabled={isAiSaving}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="connections-delete-button"
                        onClick={() => handleAiDelete(backend.id, backend.name)}
                        disabled={deleteAiMutation.isPending}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="integrations-empty">{aiSection?.meta?.emptyStateMessage}</div>
          )}

          {isAiFormOpen ? (
            <form className="connections-form" onSubmit={handleAiSubmit}>
              <label>
                Backend name
                <input
                  value={aiForm.name}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="e.g. OpenAI"
                  required
                />
              </label>
              <label>
                Provider
                <input
                  value={aiForm.provider}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, provider: event.target.value }))}
                  placeholder="Provider identifier"
                  required
                />
              </label>
              <label>
                Model
                <input
                  value={aiForm.model}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, model: event.target.value }))}
                  placeholder="Model name (optional)"
                />
              </label>
              <label>
                Status
                <select
                  value={aiForm.status}
                  onChange={(event) =>
                    setAiForm((prev) => ({
                      ...prev,
                      status: event.target.value as ConnectionStatus,
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Failover priority
                <input
                  value={aiForm.failoverPriority}
                  onChange={(event) =>
                    setAiForm((prev) => ({ ...prev, failoverPriority: event.target.value }))
                  }
                  placeholder="Numeric priority"
                />
              </label>
              <label>
                Notes
                <textarea
                  value={aiForm.notes}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, notes: event.target.value }))}
                  rows={3}
                />
              </label>
              <label>
                API key (optional)
                <input
                  value={aiForm.apiKey}
                  onChange={(event) => setAiForm((prev) => ({ ...prev, apiKey: event.target.value }))}
                  placeholder={
                    editingAiId ? "Rotate credentials via Security ▸ Secrets" : "Paste AI provider API key"
                  }
                  disabled={editingAiId !== null}
                />
              </label>
              {aiError ? <p className="connections-form__error">{aiError}</p> : null}
              <div className="connections-form__actions">
                <Button type="submit" variant="primary" disabled={isAiSaving}>
                  {isAiSaving
                    ? "Saving…"
                    : editingAiId
                      ? "Update backend"
                      : "Save backend"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setAiFormOpen(false);
                    setAiError(null);
                    setAiForm(initialAiForm);
                    setEditingAiId(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="integrations-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setAiForm(initialAiForm);
                  setEditingAiId(null);
                  setAiFormOpen(true);
                }}
              >
                Provision new model
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setUsagePanelOpen(true);
                }}
              >
                Usage analytics
              </Button>
            </div>
          )}

          <div className="connections-provider-control">
            <div className="connections-provider-control__header">
              <div>
                <h4>Manual AI provider control</h4>
                <span>
                  See current provider health and switch manually during incidents. Telegram alerts are sent to admins on errors.
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  aiProvidersQuery.refetch();
                  failoverHistoryQuery.refetch();
                }}
                disabled={aiProvidersQuery.isFetching || failoverHistoryQuery.isFetching}
              >
                Refresh
              </Button>
            </div>

            {providerActionMessage ? (
              <div className="connections-provider-alert connections-provider-alert--success">
                {providerActionMessage}
              </div>
            ) : null}
            {providerActionError ? (
              <div className="connections-provider-alert connections-provider-alert--error">
                {providerActionError}
              </div>
            ) : null}

            {aiProvidersQuery.isLoading ? (
              <div className="integrations-empty">Loading provider roster…</div>
            ) : aiProviders.length === 0 ? (
              <div className="integrations-empty">
                No AI providers registered yet. Add one to enable manual control from this panel.
              </div>
            ) : (
              <div className="connections-provider-list">
                {aiProviders.map((provider) => (
                  <div
                    key={provider.id}
                    className={`connections-provider-card${provider.isActive ? " connections-provider-card--active" : ""}`}
                  >
                    <div className="connections-provider-card__heading">
                      <div>
                        <strong>{provider.name}</strong>
                        <span className="connections-provider-card__subtitle">
                          {provider.provider}
                          {provider.model ? ` · ${provider.model}` : ""}
                          {provider.baseUrl ? ` · ${provider.baseUrl}` : ""}
                        </span>
                      </div>
                      <span className={`connections-provider-status connections-provider-status--${provider.status ?? "unknown"}`}>
                        {provider.status?.toUpperCase() ?? "UNKNOWN"}
                      </span>
                    </div>
                    <div className="connections-provider-card__meta">
                      <span>Priority {provider.priority}</span>
                      {provider.failoverPriority != null ? (
                        <span>Failover {provider.failoverPriority}</span>
                      ) : null}
                      <span>Last health: {formatTimestamp(provider.lastHealthCheck ?? null)}</span>
                    </div>
                    {provider.cooldownUntil ? (
                      <div className="connections-provider-card__hint">
                        Cooldown until {formatTimestamp(provider.cooldownUntil)}
                      </div>
                    ) : null}
                    {provider.notes ? (
                      <div className="connections-provider-card__hint connections-provider-card__hint--notes">
                        {provider.notes}
                      </div>
                    ) : null}
                    <div className="connections-provider-card__actions">
                      {provider.isActive ? (
                        <span className="connections-chip">Active</span>
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => handleProviderActivation(provider)}
                          disabled={activateProviderMutation.isPending}
                        >
                          {activateProviderMutation.isPending ? "Switching…" : "Make active"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="connections-provider-history">
              <div className="connections-provider-history__header">
                <h5>Manual switches</h5>
                <span>
                  Updated {formatTimestamp(failoverHistoryQuery.data?.refreshedAt ?? null)}
                </span>
              </div>
              {failoverHistoryQuery.isLoading ? (
                <div className="integrations-empty">Loading switch history…</div>
              ) : failoverEntries.length === 0 ? (
                <div className="integrations-empty">No manual switches recorded yet.</div>
              ) : (
                <ul className="connections-provider-history__list">
                  {failoverEntries.map((entry) => (
                    <li key={entry.id}>
                      <div className="connections-provider-history__row">
                        <div>
                          <strong>{entry.reason}</strong>
                          <span className="connections-provider-history__timestamp">
                            {formatTimestamp(entry.createdAt)}
                          </span>
                        </div>
                        <span className="connections-provider-history__actor">{entry.initiatedBy}</span>
                      </div>
                      <div className="connections-provider-history__details">
                        <span>
                          {entry.fromProviderId
                            ? `${entry.fromProviderId} → ${entry.toProviderId}`
                            : entry.toProviderId}
                        </span>
                        {entry.notes ? (
                          <span className="connections-provider-history__notes">{entry.notes}</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card
        className="overview-card connections-card"
        title="Infrastructure services"
        subtitle="Internal services used by strategies, data pipelines, and the AI stack."
      >
        <div className="section-legend">
          <span className="section-legend__dot" />
          <div>
            Register caches, message buses, and databases that the platform depends on. The list
            will populate with latency, version, and notes once services are registered.
          </div>
        </div>
        {infraSection?.items?.length ? (
          <div className="connections-matrix">
            {infraSection.items.map((service) => (
              <div key={service.id} className="connections-matrix__row">
                <div className="connections-matrix__info">
                  <div>{service.name}</div>
                  <div className="connections-matrix__meta">
                    {service.kind}
                    {service.endpoint ? ` · ${service.endpoint}` : ""}
                    {" · Last check "}
                    {formatTimestamp(service.lastCheckedAt || null)}
                  </div>
                </div>
                <div className="connections-matrix__stack">
                  <Pill tone={statusTone(service.status)}>{service.status ?? "unknown"}</Pill>
                  <div className="connections-row-actions">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleInfraEdit(service.id)}
                      disabled={isInfraSaving}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="connections-delete-button"
                      onClick={() => handleInfraDelete(service.id, service.name)}
                      disabled={deleteInfraMutation.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="integrations-empty">{infraSection?.meta?.emptyStateMessage}</div>
        )}

        {isInfraFormOpen ? (
          <form className="connections-form" onSubmit={handleInfraSubmit}>
            <label>
              Service name
              <input
                value={infraForm.name}
                onChange={(event) => setInfraForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="e.g. Redis cache"
                required
              />
            </label>
            <label>
              Kind
              <input
                value={infraForm.kind}
                onChange={(event) => setInfraForm((prev) => ({ ...prev, kind: event.target.value }))}
                placeholder="cache / database / message-bus"
                required
              />
            </label>
            <label>
              Endpoint
              <input
                value={infraForm.endpoint}
                onChange={(event) => setInfraForm((prev) => ({ ...prev, endpoint: event.target.value }))}
                placeholder="Connection string or URL"
              />
            </label>
            <label>
              Status
              <select
                value={infraForm.status}
                onChange={(event) =>
                  setInfraForm((prev) => ({
                    ...prev,
                    status: event.target.value as ConnectionStatus,
                  }))
                }
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Owner team
              <input
                value={infraForm.ownerTeam}
                onChange={(event) =>
                  setInfraForm((prev) => ({ ...prev, ownerTeam: event.target.value }))
                }
                placeholder="Ops / Platform / AI"
              />
            </label>
            <label>
              Notes
              <textarea
                value={infraForm.notes}
                onChange={(event) => setInfraForm((prev) => ({ ...prev, notes: event.target.value }))}
                rows={3}
              />
            </label>
            {infraError ? <p className="connections-form__error">{infraError}</p> : null}
            <div className="connections-form__actions">
              <Button type="submit" variant="primary" disabled={isInfraSaving}>
                {isInfraSaving
                  ? "Saving…"
                  : editingInfraId
                    ? "Update service"
                    : "Register service"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setInfraFormOpen(false);
                  setInfraError(null);
                  setInfraForm(initialInfraForm);
                  setEditingInfraId(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="integrations-actions">
            <Button
              variant="primary"
              onClick={() => {
                setInfraForm(initialInfraForm);
                setEditingInfraId(null);
                setInfraFormOpen(true);
              }}
            >
              Register service
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setManifestModalOpen(true);
                setManifestText(DEFAULT_MANIFEST_TEMPLATE);
                setManifestError(null);
                setManifestFeedback(null);
              }}
            >
              Import from manifest
            </Button>
          </div>
        )}
      </Card>

      <Card
        className="overview-card connections-card"
        title="Change log"
        subtitle="Recent actions across providers and infrastructure."
      >
        <div className="section-legend">
          <span className="section-legend__dot" />
          <div>
            Every rotation, diagnostic run, or configuration update will appear here. Use it for
            quick auditing and to keep the ops team aligned.
          </div>
        </div>
        <div className="connections-logs">
          {changeLog.length === 0 && <div className="connections-logs__line">No changes recorded yet.</div>}
          {changeLog.map((entry) => (
            <div key={entry.id} className="connections-logs__line">
              [{formatTimestamp(entry.occurredAt)}] {entry.actor} &rarr; {entry.action} {entry.target} –{" "}
              {entry.message ?? "No additional context."}
            </div>
          ))}
        </div>
        <div className="integrations-actions">
          <Button variant="ghost" onClick={handleExportLog}>
            Export log
          </Button>
          <Button
            variant="link"
            onClick={() => {
              setAlertNotice(null);
              setAlertSettingsOpen(true);
            }}
          >
            Alert settings
          </Button>
        </div>
      </Card>

      {isUsagePanelOpen ? (
        <div className="connections-overlay" role="presentation" onClick={() => setUsagePanelOpen(false)}>
          <div
            className="connections-usage-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connections-usage-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="connections-usage-header">
              <div>
                <h3 id="connections-usage-title">Usage analytics</h3>
                <p>
                  Snapshot of provider distribution, audit volume, and manual failovers. Refresh to
                  pull the latest data from the service.
                </p>
              </div>
              <div className="connections-usage-header__actions">
                <Button
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation();
                    void refetchUsageAnalytics();
                  }}
                  disabled={isUsageFetching}
                >
                  {isUsageFetching ? "Refreshing…" : "Refresh"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation();
                    setUsagePanelOpen(false);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>

            {isUsageFetching && !usageAnalytics ? (
              <div className="integrations-empty">Loading analytics…</div>
            ) : isUsageError ? (
              <div className="connections-usage-error">
                <p>Failed to load analytics: {usageErrorMessage ?? "Unknown error."}</p>
                <Button
                  variant="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    void refetchUsageAnalytics();
                  }}
                  disabled={isUsageFetching}
                >
                  Retry
                </Button>
              </div>
            ) : usageAnalytics ? (
              <div className="connections-usage-content">
                <div className="connections-usage-grid">
                  <section className="connections-usage-card">
                    <header>
                      <h4>News providers</h4>
                      <span>{usageAnalytics.newsProviders.total} registered</span>
                    </header>
                    {renderStatusBreakdown(usageAnalytics.newsProviders.statusBreakdown)}
                    <div className="connections-usage-meta">
                      <span>
                        Last update: {formatTimestamp(usageAnalytics.newsProviders.lastUpdatedAt ?? null)}
                      </span>
                    </div>
                  </section>

                  <section className="connections-usage-card">
                    <header>
                      <h4>AI backends</h4>
                      <span>
                        {usageAnalytics.aiBackends.total} configured · {usageAnalytics.aiBackends.active} active
                      </span>
                    </header>
                    {renderStatusBreakdown(usageAnalytics.aiBackends.statusBreakdown)}
                    <div className="connections-usage-meta">
                      <span>
                        Last update: {formatTimestamp(usageAnalytics.aiBackends.lastUpdatedAt ?? null)}
                      </span>
                    </div>
                  </section>

                  <section className="connections-usage-card">
                    <header>
                      <h4>Infrastructure</h4>
                      <span>{usageAnalytics.infrastructureServices.total} documented</span>
                    </header>
                    {renderStatusBreakdown(usageAnalytics.infrastructureServices.statusBreakdown)}
                    <div className="connections-usage-meta">
                      <span>
                        Last update:{" "}
                        {formatTimestamp(usageAnalytics.infrastructureServices.lastUpdatedAt ?? null)}
                      </span>
                    </div>
                  </section>
                </div>

                <section className="connections-usage-card connections-usage-card--wide">
                  <header>
                    <h4>Audit activity</h4>
                    <span>Total entries tracked: {usageAnalytics.changeLog.totalEntries}</span>
                  </header>
                  <div className="connections-usage-metrics">
                    <div>
                      <span>Last 24h</span>
                      <strong>{usageAnalytics.changeLog.last24h}</strong>
                    </div>
                    <div>
                      <span>Last 7 days</span>
                      <strong>{usageAnalytics.changeLog.last7d}</strong>
                    </div>
                    <div>
                      <span>Latest entry</span>
                      <strong>
                        {formatTimestamp(usageAnalytics.changeLog.lastEntryAt ?? null)}
                      </strong>
                    </div>
                  </div>
                  <div className="connections-usage-top-actions">
                    <h5>Top actions (7d)</h5>
                    {usageAnalytics.changeLog.topActions.length > 0 ? (
                      <ul>
                        {usageAnalytics.changeLog.topActions.map((entry) => (
                          <li key={entry.action}>
                            <span>{entry.action}</span>
                            <strong>{entry.count}</strong>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No high-volume actions recorded.</p>
                    )}
                  </div>
                </section>

                <section className="connections-usage-card connections-usage-card--wide">
                  <header>
                    <h4>Failover insights</h4>
                    <span>{usageAnalytics.failover.totalSwitches} manual switches recorded</span>
                  </header>
                  <div className="connections-usage-failover">
                    <div>
                      <span>Last switch</span>
                      <strong>{formatTimestamp(usageAnalytics.failover.lastSwitchAt ?? null)}</strong>
                    </div>
                    <div>
                      <span>Activated provider</span>
                      <strong>
                        {usageAnalytics.failover.lastSwitchToName ??
                          usageAnalytics.failover.lastSwitchToId ??
                          "—"}
                      </strong>
                    </div>
                    <div>
                      <span>Initiated by</span>
                      <strong>{usageAnalytics.failover.lastInitiatedBy ?? "—"}</strong>
                    </div>
                    <div>
                      <span>Reason</span>
                      <strong>{usageAnalytics.failover.lastReason ?? "—"}</strong>
                    </div>
                    {usageAnalytics.failover.lastNotes ? (
                      <div className="connections-usage-failover__notes">
                        Notes: {usageAnalytics.failover.lastNotes}
                      </div>
                    ) : null}
                  </div>
                </section>
              </div>
            ) : (
              <div className="integrations-empty">Analytics not loaded yet.</div>
            )}
          </div>
        </div>
      ) : null}

      {isManifestModalOpen ? (
        <div className="connections-overlay" role="presentation" onClick={closeManifestModal}>
          <div
            className="connections-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connections-manifest-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="connections-modal__header">
              <div>
                <h3 id="connections-manifest-title">Import infrastructure manifest</h3>
                <p>
                  Paste a JSON manifest containing a <code>services</code> array or an array of service
                  objects. Each entry requires a name and kind.
                </p>
              </div>
              <Button variant="ghost" onClick={closeManifestModal}>
                Close
              </Button>
            </div>
            <form className="connections-modal__body" onSubmit={handleManifestImport}>
              <label>
                Manifest payload
                <textarea
                  value={manifestText}
                  onChange={(event) => setManifestText(event.target.value)}
                  rows={12}
                  spellCheck={false}
                  data-testid="manifest-textarea"
                />
              </label>
              {manifestError ? <p className="connections-form__error">{manifestError}</p> : null}
              {manifestFeedback ? (
                <p className="connections-modal__feedback" data-testid="manifest-feedback">
                  {manifestFeedback}
                </p>
              ) : null}
              <div className="connections-modal__actions">
                <Button type="submit" variant="primary" disabled={isManifestImporting}>
                  {isManifestImporting ? "Importing…" : "Import manifest"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setManifestText(DEFAULT_MANIFEST_TEMPLATE);
                    setManifestError(null);
                    setManifestFeedback(null);
                  }}
                  disabled={isManifestImporting}
                >
                  Reset template
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isAlertSettingsOpen ? (
        <div
          className="connections-overlay"
          role="presentation"
          onClick={() => {
            setAlertSettingsOpen(false);
          }}
        >
          <div
            className="connections-modal connections-modal--narrow"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connections-alert-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="connections-modal__header">
              <div>
                <h3 id="connections-alert-title">Alert routing preferences</h3>
                <p>
                  Choose where operations alerts should appear. Preferences are stored locally in this
                  browser.
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setAlertSettingsOpen(false);
                }}
              >
                Close
              </Button>
            </div>
            <div className="connections-alert__body">
              <label className="connections-alert__toggle">
                <input type="checkbox" checked={alertPrefs.inApp} onChange={handleAlertToggle("inApp")} />
                <div>
                  <strong>In-app notifications</strong>
                  <span>Display banners and provider alerts within the dashboard.</span>
                </div>
              </label>
              <label className="connections-alert__toggle">
                <input type="checkbox" checked={alertPrefs.telegram} onChange={handleAlertToggle("telegram")} />
                <div>
                  <strong>Telegram bot</strong>
                  <span>Send incidents to the configured Telegram channel.</span>
                </div>
              </label>
              <label className="connections-alert__toggle">
                <input
                  type="checkbox"
                  checked={alertPrefs.emailDigest}
                  onChange={handleAlertToggle("emailDigest")}
                />
                <div>
                  <strong>Email digests</strong>
                  <span>Receive a daily summary of connection health (beta).</span>
                </div>
              </label>
              {alertNotice ? <p className="connections-modal__feedback">{alertNotice}</p> : null}
              <div className="connections-modal__actions">
                <Button type="button" variant="ghost" onClick={handleResetAlerts}>
                  Reset defaults
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setAlertSettingsOpen(false);
                  }}
                >
                  Done
                </Button>
              </div>
              <p className="connections-alert__hint">
                Configure additional recipients from Security ▸ Admin notifications. Telegram delivery
                requires the bot token to be provisioned.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
